import 'core-js/modules/es.object.assign.js';
import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import classNames from 'classnames';
import { UserIcon } from '@cognite/cogs-icons';
import { getInitials } from '@cognite/cogs-utils';
import '../../tokens/color-tokens/index.js';
import '../../tokens/elevation-tokens/index.js';
import { tokenToPixelSize } from '../../tokens/size-tokens/size-to-pixels.js';
import 'core-js/modules/es.array.concat.js';
import '@mui/joy';
import { NotificationDot } from '../notification-dot/notification-dot.js';
import { PresenceDot } from '../presence-dot/presence-dot.js';
import { Squircle } from '../squircle/squircle.js';
import { Tooltip } from '../tooltip/tooltip.js';
import { getAvatarColor } from './utils/get-avatar-color/get-avatar-color.js';

var AVATAR_TOOLTIP_DELAY = 500;
/**
 * Avatars are a visual representation of a user or entity that supports images,
 * icons or letters.
 */
var Avatar = /*#__PURE__*/forwardRef(function (_a, ref) {
  var image = _a.image,
    _a$tooltip = _a.tooltip,
    tooltip = _a$tooltip === undefined ? true : _a$tooltip,
    text = _a.text,
    _a$size = _a.size,
    size = _a$size === undefined ? 'medium' : _a$size,
    className = _a.className,
    presenceDot = _a.presenceDot,
    onClick = _a.onClick,
    icon = _a.icon,
    notification = _a.notification,
    stacked = _a.stacked,
    style = _a.style,
    ariaLabel = _a['aria-label'],
    tooltipProps = _a.tooltipProps,
    otherProps = __rest(_a, ["image", "tooltip", "text", "size", "className", "presenceDot", "onClick", "icon", "notification", "stacked", "style", 'aria-label', "tooltipProps"]);
  var finalText = text && getInitials(text, true);
  var background = getAvatarColor(text);
  var technical = size === 'tiny' || size === 'x-small';
  var getAvatarType = function getAvatarType() {
    if (image) return 'image';
    if (icon) return 'icon';
    if (text) return 'text';
    return 'icon';
  };
  var type = getAvatarType();
  // This is a temporary fix because the design was made before the size tokens were
  // introduced. Should be fixed on the design side first, then we can remove this
  var sizeTokenOverrides = function sizeTokenOverrides(sizeString) {
    if (sizeString === 'large') return 40;
    return tokenToPixelSize(sizeString);
  };
  var isInteractive = !!onClick || tooltip || text;
  var avatarProps = Object.assign({
    ref: ref,
    className: classNames('cogs-v10', 'cogs-avatar__button', "cogs-avatar--size-" + size, "cogs-avatar--background-" + background, "cogs-avatar--type-" + type, technical && 'cogs-avatar--technical', "cogs-avatar--" + (isInteractive ? 'interactive' : 'static'), stacked && "cogs-avatar--stacked", className),
    'aria-label': ariaLabel || text ? "Profile picture of " + text : 'Profile picture',
    style: image ? Object.assign(Object.assign({}, style), {
      backgroundImage: "url(\"" + image + "\")"
    }) : style
  }, otherProps);
  var renderAvatar = function renderAvatar() {
    return jsx(Tooltip, Object.assign({
      enterDelay: AVATAR_TOOLTIP_DELAY,
      enterNextDelay: AVATAR_TOOLTIP_DELAY,
      className: "cogs-v10 cogs-avatar-tooltip",
      content: text,
      disabled: !tooltip || !text,
      wrapped: true
    }, tooltipProps, {
      children: jsx(Squircle, {
        stacked: stacked,
        size: sizeTokenOverrides(size),
        outline: type === 'image',
        "data-testid": "cogs-avatar",
        children: onClick ? jsxs("button", Object.assign({
          type: "button",
          onClick: onClick,
          disabled: technical,
          "aria-hidden": otherProps['aria-hidden'] || !text && !onClick,
          tabIndex: otherProps['aria-hidden'] || !text && !onClick ? -1 : 0
        }, avatarProps, {
          children: [type === 'text' && finalText, type === 'icon' && jsx(UserIcon, {})]
        })) : jsxs("div", Object.assign({}, avatarProps, {
          children: [type === 'text' && finalText, type === 'icon' && jsx(UserIcon, {})]
        }))
      })
    }));
  };
  var renderNotificationOrPresenceDot = function renderNotificationOrPresenceDot() {
    if (notification) {
      return jsx(NotificationDot, {
        children: renderAvatar()
      });
    }
    return jsx(PresenceDot, {
      hidden: !presenceDot,
      status: presenceDot,
      children: renderAvatar()
    });
  };
  return jsx("div", {
    className: "cogs-v10 cogs-avatar",
    children: technical ? renderAvatar() : renderNotificationOrPresenceDot()
  });
});

export { Avatar };
